import React, { useEffect, useMemo } from 'react';

import AIPromptInput from './AIPromptInput';
import UseCaseSelector from './UseCaseSelector';
import FilmStripSlider from './components/FilmStripSlider';
import LoadingState from './components/LoadingState';
import VideoPlayer from './components/VideoPlayer';
import {
  useVideoMetadata,
  useVideoPlayback,
  useVideoSelection,
  useVideoUI,
} from './context/VideoEditorContext';
import { useCases, MAX_SELECTION_DURATION } from './utils/constants';

interface VideoEditorUIProps {
  onGenerateVideo: () => void;
}

const VideoEditorUI: React.FC<VideoEditorUIProps> = ({ onGenerateVideo }) => {
  const { videoSrc, videoMetadata, error, isLoadingMetadata } =
    useVideoMetadata();
  const { isPlaying, currentTime, handlePlayPause, handleTimeUpdate, seekTo } =
    useVideoPlayback();
  const { selection, handleSelectionChange } = useVideoSelection();
  const { selectedUseCase, defaultPlaceholder, handleUseCaseSelect } =
    useVideoUI();

  const currentSuggestions = useMemo(() => {
    if (!selectedUseCase) return [];
    const useCase = useCases.find((uc) => uc.id === selectedUseCase);
    return useCase ? useCase.suggestions : [];
  }, [selectedUseCase]);

  useEffect(() => {
    if (videoMetadata && !selection) {
      const initialSelection = {
        startTime: 0,
        endTime: Math.min(videoMetadata.duration, MAX_SELECTION_DURATION),
        duration: Math.min(videoMetadata.duration, MAX_SELECTION_DURATION),
      };
      handleSelectionChange(initialSelection);
    }
  }, [videoMetadata, selection, handleSelectionChange]);

  // Auto-play when modal opens
  useEffect(() => {
    if (videoMetadata && videoSrc) {
      const timer = setTimeout(() => {
        handlePlayPause();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [videoMetadata, videoSrc, handlePlayPause]);

  if (isLoadingMetadata || error) {
    return (
      <LoadingState
        progress={50}
        message={error ? `Error: ${error}` : 'Loading video...'}
      />
    );
  }

  if (!videoSrc || !videoMetadata) {
    return null;
  }

  return (
    <>
      <div className="w-full flex flex-col">
        <div className="flex flex-col w-full">
          <VideoPlayer
            src={videoSrc}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onTimeUpdate={handleTimeUpdate}
            currentTime={currentTime}
            className="w-full"
          />

          {selection && (
            <FilmStripSlider
              selection={selection}
              onSelectionChange={handleSelectionChange}
              onSeek={seekTo}
            />
          )}
        </div>
      </div>

      <UseCaseSelector
        useCases={useCases}
        selectedUseCase={selectedUseCase}
        onUseCaseSelect={(id) => handleUseCaseSelect(id, useCases)}
      />

      <AIPromptInput
        onGenerateVideo={onGenerateVideo}
        suggestions={currentSuggestions}
        defaultPlaceholder={defaultPlaceholder}
      />
    </>
  );
};

VideoEditorUI.displayName = 'VideoEditorUI';

export default VideoEditorUI;
